<template>
  <AuthLayout
    sidebar-title="Two ways to sign in safely"
    sidebar-text="We recommend the magic link - it's the fastest and easiest way in, with no password to remember."
    :support-phone="supportPhone"
    title="How would you like to sign in?"
    subtitle="Both options use secure, encrypted connections"
    :card="false"
  >
    <form action="javascript:void(0)" @submit.prevent="handleSubmit">
      <div
        role="radiogroup"
        aria-label="Choose how to sign in"
        @keydown="onGroupKeydown"
      >
        <button
          type="button"
          role="radio"
          class="auth-login__option"
          :class="{ 'auth-login__option--active': selectedMethod === SIGN_IN_METHOD.MAGIC_LINK }"
          :aria-checked="selectedMethod === SIGN_IN_METHOD.MAGIC_LINK ? 'true' : 'false'"
          :tabindex="selectedMethod === SIGN_IN_METHOD.MAGIC_LINK || !selectedMethod ? 0 : -1"
          :disabled="loading"
          @click="selectMethod(SIGN_IN_METHOD.MAGIC_LINK)"
        >
          <div class="auth-login__option-header">
            <span class="auth-login__option-title">
              <AuthIcon name="link" class="auth-login__option-icon" />
              Send me a magic link
            </span>
            <span class="auth-login__option-badge">Preferred</span>
          </div>
          <p class="auth-login__option-desc">
            We'll email you a secure link. Click it to continue signing in, then verify with the code sent to your phone.
          </p>
        </button>

        <button
          type="button"
          role="radio"
          class="auth-login__option"
          :class="{ 'auth-login__option--active': selectedMethod === SIGN_IN_METHOD.PASSWORD }"
          :aria-checked="selectedMethod === SIGN_IN_METHOD.PASSWORD ? 'true' : 'false'"
          :tabindex="selectedMethod === SIGN_IN_METHOD.PASSWORD ? 0 : -1"
          :disabled="loading"
          @click="selectMethod(SIGN_IN_METHOD.PASSWORD)"
        >
          <span class="auth-login__option-title">Use my password</span>
          <p class="auth-login__option-desc">
            Enter your password, then verify with the code sent to your phone or email.
          </p>
        </button>
      </div>

      <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading" :disabled="!selectedMethod">
          Continue Sign In
        </AuthButton>
      </div>
    </form>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('cancel')">
      ← Back
    </button>
  </AuthLayout>
</template>

<script setup>
import { shallowRef } from 'vue'
import { isOptionNavigationKey, moveOptionSelection } from '../utils/optionRadiogroup.js'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'
import AuthButton from '../../shared/components/AuthButton.vue'

defineProps({
  loading:      { type: Boolean, default: false },
  error:        { type: String, default: '' },
  supportPhone: { type: String, default: '815-908-2020' },
})

const emit = defineEmits(['magic-link', 'password', 'cancel'])

const SIGN_IN_METHOD = Object.freeze({
  MAGIC_LINK: 'magic-link',
  PASSWORD: 'password',
})
const signInMethods = [SIGN_IN_METHOD.MAGIC_LINK, SIGN_IN_METHOD.PASSWORD]
const selectedMethod = shallowRef(null)

const selectMethod = (method) => {
  selectedMethod.value = method
}

const onGroupKeydown = (event) => {
  if (!isOptionNavigationKey(event.key)) return

  event.preventDefault()
  selectedMethod.value = moveOptionSelection(signInMethods, selectedMethod.value, event.key)
}

const handleSubmit = () => {
  if (!selectedMethod.value) return
  emit(selectedMethod.value)
}
</script>
